import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../../assets/assetKeys';
import { GAME_WIDTH } from '../../data/gameConstants';
import { Hazard } from '../../objects/Hazard';
import { Player } from '../../objects/Player';
import { ScoreSystem } from '../../systems/ScoreSystem';
import { AudioSystem } from '../../systems/AudioSystem';
import { UIStyle } from '../../ui/UIStyle';

interface SpawnEntry {
  time: number;
  type: 'tree' | 'crack';
  x: number;
  y: number;
  warnMs?: number;
}

const SPAWN_SCHEDULE: SpawnEntry[] = [
  { time: 2000, type: 'crack', x: 480, y: 565 },
  { time: 5000, type: 'tree', x: 300, y: 430, warnMs: 2000 },
  { time: 10000, type: 'crack', x: 700, y: 558 },
  { time: 14000, type: 'tree', x: 860, y: 420, warnMs: 2000 },
  { time: 20000, type: 'crack', x: 540, y: 568 },
  { time: 24000, type: 'tree', x: 680, y: 425, warnMs: 2000 },
];

export class Zone2EarthquakeEvent {
  private scene: Phaser.Scene;
  private player: Player;
  private score: ScoreSystem;
  private onHazardHit?: () => void;
  private durationMs: number;
  private elapsed = 0;
  private active = false;
  private hitCount = 0;
  private hazards: Hazard[] = [];
  private warnings: Phaser.GameObjects.Arc[] = [];
  private dustParticles: Phaser.GameObjects.Arc[] = [];
  private timerText?: Phaser.GameObjects.Text;
  private onComplete?: (noHits: boolean) => void;
  private spawnIndex = 0;
  private shakeTimer?: Phaser.Time.TimerEvent;

  constructor(
    scene: Phaser.Scene,
    player: Player,
    score: ScoreSystem,
    durationMs = 30000,
    onHazardHit?: () => void,
  ) {
    this.scene = scene;
    this.player = player;
    this.score = score;
    this.durationMs = durationMs;
    this.onHazardHit = onHazardHit;
  }

  start(onComplete: (noHits: boolean) => void): void {
    this.active = true;
    this.elapsed = 0;
    this.hitCount = 0;
    this.spawnIndex = 0;
    this.onComplete = onComplete;

    AudioSystem.playSfx('EARTHQUAKE', { volume: 0.95 });
    AudioSystem.startEarthquakeRumble();

    this.timerText = this.scene.add.text(GAME_WIDTH / 2, 110, '', {
      ...UIStyle.onBrightBg(UIStyle.px(20), '#ff6b6b', true),
      backgroundColor: '#0a1218ee',
      padding: { x: 14, y: 8 },
    }).setOrigin(0.5).setDepth(200).setScrollFactor(0);

    this.shakeTimer = this.scene.time.addEvent({
      delay: 800,
      loop: true,
      callback: () => {
        if (this.active) this.scene.cameras.main.shake(300, 0.008);
      },
    });

    this.createDust();
  }

  update(delta: number): void {
    if (!this.active) return;

    this.elapsed += delta;
    const remaining = Math.ceil((this.durationMs - this.elapsed) / 1000);
    this.timerText?.setText(`⏱ Bertahan: ${Math.max(0, remaining)}s`);

    while (this.spawnIndex < SPAWN_SCHEDULE.length
      && this.elapsed >= SPAWN_SCHEDULE[this.spawnIndex].time) {
      this.scheduleSpawn(SPAWN_SCHEDULE[this.spawnIndex]);
      this.spawnIndex++;
    }

    if (this.elapsed >= this.durationMs) {
      this.end();
    }
  }

  isActive(): boolean {
    return this.active;
  }

  private scheduleSpawn(entry: SpawnEntry): void {
    if (entry.type === 'tree' && entry.warnMs) {
      const warn = this.scene.add.circle(entry.x, entry.y, 40, 0xff6b00, 0.35);
      warn.setStrokeStyle(3, 0xffd166, 0.9).setDepth(14);
      this.warnings.push(warn);
      this.scene.tweens.add({
        targets: warn,
        scaleX: 1.3,
        scaleY: 1.3,
        alpha: 0.15,
        duration: 400,
        yoyo: true,
        repeat: Math.floor(entry.warnMs / 400),
      });
      this.scene.time.delayedCall(entry.warnMs, () => {
        warn.destroy();
        this.spawnFallingTree(entry.x, entry.y);
      });
    } else {
      this.spawnCrack(entry.x, entry.y);
    }
  }

  private spawnFallingTree(x: number, y: number): void {
    const tree = this.scene.add.image(x, y - 40, TEXTURE_KEYS.TREE_LARGE).setScale(0.6).setDepth(10);
    this.scene.tweens.add({
      targets: tree,
      angle: 90,
      y: y + 10,
      duration: 600,
      ease: 'Quad.easeIn',
      onComplete: () => {
        tree.setTexture(TEXTURE_KEYS.TREE_FALLEN);
        tree.setAngle(0).setScale(0.7);
        const hazard = new Hazard(this.scene, x, y + 10, {
          id: `falling_tree_${x}_${y}`,
          type: 'falling_tree',
          onHit: () => this.onHit(),
        });
        hazard.setScale(0.75);
        this.hazards.push(hazard);
        this.scene.physics.add.overlap(this.player, hazard, () => {
          hazard.onPlayerOverlap(this.player, this.score);
        });
      },
    });
  }

  private spawnCrack(x: number, y: number): void {
    const warn = this.scene.add.circle(x, y, 30, 0xff0000, 0.25).setDepth(13);
    this.warnings.push(warn);
    this.scene.time.delayedCall(1000, () => {
      warn.destroy();
      const hazard = new Hazard(this.scene, x, y, {
        id: `crack_${x}_${y}`,
        type: 'ground_crack',
        onHit: () => this.onHit(),
      });
      hazard.setScale(0.85);
      this.hazards.push(hazard);
      this.scene.physics.add.overlap(this.player, hazard, () => {
        hazard.onPlayerOverlap(this.player, this.score);
      });
    });
  }

  private onHit(): void {
    this.hitCount++;
    this.scene.cameras.main.flash(150, 255, 80, 80);
    AudioSystem.playEarthquakeImpact();
    this.onHazardHit?.();
  }

  private createDust(): void {
    for (let i = 0; i < 8; i++) {
      const d = this.scene.add.circle(
        Phaser.Math.Between(100, 1180),
        Phaser.Math.Between(300, 600),
        Phaser.Math.Between(4, 10),
        0x8d6e63,
        0.3,
      ).setDepth(8);
      this.dustParticles.push(d);
      this.scene.tweens.add({
        targets: d,
        x: d.x + Phaser.Math.Between(-30, 30),
        y: d.y - Phaser.Math.Between(10, 40),
        alpha: 0,
        duration: Phaser.Math.Between(800, 1600),
        yoyo: true,
        repeat: -1,
      });
    }
  }

  private end(): void {
    this.active = false;
    AudioSystem.stopEarthquakeRumble();
    this.shakeTimer?.destroy();
    this.timerText?.destroy();
    this.warnings.forEach((w) => w.destroy());
    this.dustParticles.forEach((d) => d.destroy());
    this.hazards.forEach((h) => h.deactivate());

    const noHits = this.hitCount === 0;
    this.onComplete?.(noHits);
    this.onComplete = undefined;
  }

  destroy(): void {
    this.cancel();
  }

  /** Bersihkan event tanpa memicu callback selesai (penting saat ulangi zona). */
  cancel(): void {
    this.active = false;
    AudioSystem.stopEarthquakeRumble();
    this.onComplete = undefined;
    this.shakeTimer?.destroy();
    this.shakeTimer = undefined;
    this.timerText?.destroy();
    this.timerText = undefined;
    this.warnings.forEach((w) => w.destroy());
    this.warnings = [];
    this.dustParticles.forEach((d) => d.destroy());
    this.dustParticles = [];
    this.hazards.forEach((h) => h.deactivate());
    this.hazards = [];
    this.scene.cameras.main.resetFX();
  }
}
